import { useQuery, useMutation } from "@tanstack/react-query";
import {
  fetch3,
  fetch4,
  fetch5,
  fetch7,
  fetch8,
} from "../commonApis/commonApis";
import { useDispatch } from "react-redux";
import { addStaff, setStaffList } from "../features/staff/staffSlice";
const url = process.env.REACT_APP_BASE_URL;

// interface CountryPayload {
//   countryName?: string;
//   countryCode: string;
//   currencyCode: string;
//   dialCode: number;
//   id?: string;
//   type?: string;
// }

// interface HubPayload {
//   id?: string;
//   type?: string;
//   name?: string;
//   hubName?: string;
//   countryId: string;
// }

// interface CityPayload {
//   id?: string;
//   name: string;
//   cityCode: string;
//   arabicName: string;
//   countryId: string;
//   hubId: string;
//   type?: string;
// }

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
  statusCode?: number;
  statusMessage?: string;
  error?: any;
}

interface StaffPayload {
  id?: string;
  email: string;
  fullName: string;
  city: string;
  country: string;
  phone: string;
  state: string;
  address?: string;
  iqamaNo?: string;
  staffRolesId?: string;
  type?: string;
}

// interface StaffUpdatePayload {
//   id?: string;
//   type?: string;
//   userEmail: string;
//   updateData: {
//     status: string;
//   };
// }

type StaffStatus = {
  status: string;
};

export const useStaffsData = (
  page: number,
  limit: number,
  searchFor: string,
) => {
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ["staffsData", page, limit, searchFor],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/lm-staff?page=${page}&limit=${limit}&searchFor=${searchFor}&type=LM`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }
      dispatch(setStaffList(res.data));
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchStaffsAllData = (warehouse?: string) => {
  return useQuery({
    queryKey: ["staffsAllData", warehouse],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/lm-staff/all-staffs?type=LM&warehouse=${warehouse ?? ""}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchStaffsAllDataForWarehouseMerchant = (clientId: string) => {
  return useQuery({
    queryKey: ["staffsAllDataWarehouseMerchant"],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/lm-staff/all-staffs?type=LM&client=${clientId}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
  });
};

// outlet assigned staff api
export const useOutletAssignedStaffs = (warehouseId?: string) => {
  return useQuery({
    queryKey: ["outletAssignedStaffs", warehouseId],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/lm-staff/all-staffs/${warehouseId}`, "get");
      if (!res || !res.data) return res.message;
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

 

export const useStaffDetailsData = (id?: string) => {
  return useQuery({
    queryKey: ["staffDetailsData", id],
    queryFn: async () => {
      if (!id) return null; // 👈 early exit inside the hook logic
      const res = await fetch3(`${url}/api/lm-staff/${id}?type=LM`, "get");
      if (!res || !res.data) return res.message;
      return res.data;
    },
    enabled: !!id, // 👈 run only when id exists
    staleTime: 1000 * 60 * 5,
  });
};

export const useStaffDocsData = (id?: string) => {
  return useQuery({
    queryKey: ["staffDocsData", id],
    queryFn: async () => {
      if (!id) return null; // 👈 early exit inside the hook logic
      const res = await fetch3(`${url}/api/staff/${id}/docs?type=LM`, "get");
      if (!res || !res.data) return res.message;
      return res.data;
    },
    enabled: !!id, // 👈 run only when id exists
    staleTime: 1000 * 60 * 5,
  });
};

const buildStaffFormData = (payload: Record<string, any>): FormData => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (key === "warehouses" && Array.isArray(value)) {
      value.forEach((warehouse: string) =>
        formData.append("warehouses", warehouse)
      );
    } else if (key === "outlets" && Array.isArray(value)) {
      value.forEach((outlet: string) =>
        formData.append("outlets", outlet)
      );
    } else if (value instanceof File) {
      formData.append(key, value);
    } else if (Array.isArray(value)) {
      value.forEach((item: any) => formData.append(key, item));
    } else {
      formData.append(key, value as string);
    }
  });

  return formData;
};

export const useStaffCreateMutation = () => {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: StaffPayload) => {
      const formData = buildStaffFormData(payload as any);

      const res = await fetch(`${url}/api/lm-staff`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const data = await res.json();

      if (!data.success) {
        throw data.message || data?.error || "Something went wrong";
      }

      return data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Staff Created successfully:", data);
      dispatch(addStaff(data?.data));
    },
    onError: (error: any) => {
      console.error("❌ Staff create request failed:", error.message);
      return error;
    },
  });
};


export const useStaffUpdateMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const { id, ...rest } = payload;

      const formData = buildStaffFormData(rest);

      const res = await fetch(`${url}/api/lm-staff/${id}`, {
        method: "PUT",
        body: formData,
        credentials: "include",
      });

      const data: ApiResponse = await res.json();

      if (!data.success) {
        throw data.message || data?.error || "Something went wrong";
      }

      return data;
    },
    onSuccess: (data) => {
      console.log("✅ Staff updated successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Staff update request failed:", error.message);
      return error;
    },
  });
};

export const useStaffStatusMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: StaffStatus) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/lm-staff/${id}`,
        "PATCH",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Staff status updated successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ staff status update request failed:", error.message);
    },
  });
};

export const useStaffRoleCreateMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(`${url}/api/roles`, payload);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Staff role Created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ staff role create request failed:", error.message);
    },
  });
};

export const useStaffRoleUpdateMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/roles/${payload?.id}`,
        "PATCH",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Staff role updated successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ staff role update request failed:", error.message);
    },
  });
};

export const useStaffRoleDeleteMutation = () => {
  return useMutation({
    mutationFn: async (roleId: string) => {
      const res: ApiResponse = await fetch7(
        `${url}/api/roles/${roleId}`,
        "DELETE",
        null,
      );

      if (!res.success) {
        throw new Error(res.message || "Failed to delete role");
      }

      return res.data;
    },
    onSuccess: (data) => {
      console.log("✅ Staff role deleted successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ staff role delete request failed:", error.message);
    },
  });
};

export const useRolesData = () => {
  return useQuery({
    queryKey: ["rolesData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/roles?type=LM`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useStaffBulkDocMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch8(
        `${url}/api/bulk/bulk-staff`,
        "POST",
        payload,
      );


      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res;
    },
    onSuccess: (res: any) => {
      console.log("Staff Created successfully:", res);
      return res;
    },
    onError: (error: any) => {
      console.error("Staff create request failed:", error.message);
    },
  });
};

export const useFetchStaffTemplate = () => {
  return useQuery({
    queryKey: ["templates"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/templates/staffs`, "get");
      if (!res || !res.data) return res.message;
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};


// Outlet roles

export const useOutletRolesData = () => {
  return useQuery({
    queryKey: ["outletRolesData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/outletstaffrole`, "get");
      if (!res || !res.data) return res.message;
      return res.data; 
    },
    staleTime: 1000 * 60 * 5,
  });
};
 

export const useOutletRoleCreateMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(
        `${url}/api/outletstaffrole`,
        payload
      );
      if (!res.success) throw new Error(res.message || "Something went wrong");
      return res.data;
    },
    onSuccess: (data: any) => console.log("✅ Outlet role created:", data),
    onError: (error: any) => console.error("❌ Outlet role create failed:", error.message),
  });
};
 

export const useOutletRoleUpdateMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/outletstaffrole/${payload?.id}`,
        "PATCH",
        payload
      );
      if (!res.success) throw new Error(res.message || "Something went wrong");
      return res.data;
    },
    onSuccess: (data: any) => console.log("✅ Outlet role updated:", data),
    onError: (error: any) => console.error("❌ Outlet role update failed:", error.message),
  });
};
 

export const useOutletRoleDeleteMutation = () => {
  return useMutation({
    mutationFn: async (roleId: string) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/outletstaffrole/${roleId}`,
        "DELETE",
        {}
      );
      if (!res.success) throw new Error(res.message || "Something went wrong");
      return res.data;
    },
    onSuccess: (data: any) => console.log("✅ Outlet role deleted:", data),
    onError: (error: any) => console.error("❌ Outlet role delete failed:", error.message),
  });
};
 